/**
 * IndexedDB 위의 migration 실행 (계획서 §8.3, SYNC_SECURITY §14).
 *   1) 호환성 검사 (데이터가 앱보다 새로우면 중단)
 *   2) pre-migration 스냅샷
 *   3) 전 레코드 복호화 → 순수 엔진(migrateRecords)으로 변환·검증 (레코드 수·id 집합 일치)
 *   4) 재암호화 후 단일 트랜잭션으로 commit (revision+1, 이전 revision은 history에 그대로)
 *   5) 실패 시 아무것도 쓰지 않음 → 원본 그대로, 스냅샷 보존
 */
import { ENTITY_SCHEMAS, SCHEMA_VERSION, type EntityType } from '@/domain/schema/v1';
import { migrateRecords, type MigrationIssue, type MigrationRegistry, type VersionedRecord } from '@/domain/migrations';
import { openJson, sealJson, type DataKey } from './crypto/envelope';
import { META, type CounselDb, type StoredRecord } from './db/schema';
import { recordAad } from './repo/repository';
import { createSnapshot } from './snapshots';

export class NewerDataError extends Error {
  constructor(dataVersion: number, appVersion: number) {
    super(`데이터(v${dataVersion})가 앱(v${appVersion})보다 새롭습니다. 앱을 업데이트하세요.`);
    this.name = 'NewerDataError';
  }
}

export class MigrationFailedError extends Error {
  constructor(public readonly issues: MigrationIssue[]) {
    super(`migration 실패 (${issues.length}건). 기존 데이터는 변경되지 않았습니다.`);
    this.name = 'MigrationFailedError';
  }
}

export type MigrationCtx = { db: CounselDb; dataKey: DataKey; vaultId: string; deviceId: string; now?: () => Date };

export const defaultValidate = (r: VersionedRecord): string | null => {
  const schema = ENTITY_SCHEMAS[r.entityType as EntityType];
  if (!schema) return 'unknown-entity-type';
  const res = schema.safeParse(r);
  return res.success ? null : res.error.issues.map((i) => `${i.path.join('.')}:${i.code}`).join(',');
};

export async function runMigrations(
  ctx: MigrationCtx,
  registry: MigrationRegistry,
  targetVersion: number = SCHEMA_VERSION,
  validate: (r: VersionedRecord) => string | null = defaultValidate,
): Promise<{ migrated: boolean; from: number; to: number; count: number }> {
  const { db } = ctx;
  const from = ((await db.meta.get(META.dataSchemaVersion))?.value as number | undefined) ?? targetVersion;
  if (from > targetVersion) throw new NewerDataError(from, targetVersion);

  const rows = await db.entities.toArray();
  const needs = rows.some((r) => r.schemaVersion < targetVersion);
  if (from === targetVersion && !needs) return { migrated: false, from, to: targetVersion, count: 0 };

  await createSnapshot(db, 'pre-migration');

  const decrypted: VersionedRecord[] = [];
  for (const r of rows) decrypted.push(await openJson<VersionedRecord>(ctx.dataKey, r.env, recordAad(ctx.vaultId, r)));

  const result = migrateRecords(decrypted, targetVersion, registry, validate);
  if (!result.ok) throw new MigrationFailedError(result.issues);

  const at = (ctx.now?.() ?? new Date()).toISOString();
  const byKey = new Map(rows.map((r) => [`${r.entityType}:${r.id}`, r]));
  const changed: StoredRecord[] = [];
  for (const rec of result.records) {
    const before = byKey.get(`${rec.entityType}:${rec.id}`)!;
    if (before.schemaVersion === rec.schemaVersion) continue;
    const next = {
      ...rec,
      revision: before.revision + 1,
      updatedAt: at,
      updatedBy: 'system',
      lastModifiedDevice: ctx.deviceId,
    } as VersionedRecord & { revision: number };
    changed.push({
      ...before,
      revision: next.revision,
      schemaVersion: next.schemaVersion,
      updatedAt: at,
      env: await sealJson(
        ctx.dataKey,
        next,
        recordAad(ctx.vaultId, { ...before, revision: next.revision, schemaVersion: next.schemaVersion }),
      ),
    });
  }

  await db.transaction('rw', db.entities, db.history, db.outbox, db.meta, async () => {
    // 그 사이 다른 쓰기가 있었으면 중단 (조용한 덮어쓰기 금지)
    for (const r of changed) {
      const cur = await db.entities.get([r.entityType, r.id]);
      if (!cur || cur.revision !== r.revision - 1)
        throw new MigrationFailedError([{ entityType: r.entityType, id: r.id, code: 'concurrent-write' }]);
    }
    await db.entities.bulkPut(changed);
    await db.history.bulkPut(changed);
    await db.outbox.bulkAdd(
      changed.map((r) => ({ entityType: r.entityType, id: r.id, revision: r.revision, queuedAt: at })),
    );
    await db.meta.put({ key: META.dataSchemaVersion, value: targetVersion });
    const log = ((await db.meta.get('migrationLog'))?.value as unknown[] | undefined) ?? [];
    await db.meta.put({ key: 'migrationLog', value: [...log, { from, to: targetVersion, at, count: changed.length }] });
  });
  return { migrated: true, from, to: targetVersion, count: changed.length };
}
